// src/pages/LocalInventoryAdjustment.js

import React, { useState, useEffect } from 'react';
import ApiClient from '../utils/ApiClient';

function LocalInventoryAdjustment() {
    const [searchQuery, setSearchQuery] = useState('');
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [locations, setLocations] = useState([]);
    const [updateStatus, setUpdateStatus] = useState({});

    // Fetch location options when component mounts
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locationsResponse = await ApiClient.post("/api/sflocalinventorylocations");
                if (locationsResponse?.data) {
                    setLocations(locationsResponse.data);
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
                setError("Failed to load location options");
            }
        };

        fetchLocations();
    }, []);

    // Handle search submission
    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        setLoading(true);
        setError(null);

        try {
            const response = await ApiClient.post("/api/searchlocalinventory", {
                identifier: searchQuery
            });

            if (response?.data) {
                setInventoryItems(response.data);
            } else {
                setInventoryItems([]);
                setError("No items found");
            }
        } catch (error) {
            console.error("Error searching inventory:", error);
            setError("Failed to search inventory");
            setInventoryItems([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle location change for an item
    const handleLocationChange = async (itemId, newLocation) => {
        // Update UI state first for responsiveness
        setUpdateStatus(prev => ({ ...prev, [itemId]: 'loading' }));
        
        try {
            const response = await ApiClient.post("/api/updatelocalinventorylocation", {
                id: itemId,
                location: newLocation
            });

            if (response?.data?.success) {
                // Update the item in the local state to reflect the change
                setInventoryItems(prevItems => 
                    prevItems.map(item => 
                        item.id === itemId ? { ...item, location: newLocation } : item
                    )
                );
                setUpdateStatus(prev => ({ ...prev, [itemId]: 'success' }));
                
                // Clear success status after 3 seconds
                setTimeout(() => {
                    setUpdateStatus(prev => {
                        const newState = { ...prev };
                        delete newState[itemId];
                        return newState;
                    });
                }, 3000);
            } else {
                setUpdateStatus(prev => ({ ...prev, [itemId]: 'error' }));
            }
        } catch (error) {
            console.error("Error updating location:", error);
            setUpdateStatus(prev => ({ ...prev, [itemId]: 'error' }));
        }
    };

    return (
        <div className="space-y-8 px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">Local Inventory Adjustment</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Search inventory items by identifier and update their locations.
                    </p>
                </div>
            </div>

            {/* Search Form */}
            <div className="mt-4">
                <form onSubmit={handleSearch} className="flex space-x-4">
                    <div className="flex-1">
                        <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                            Search by Identifier (ASIN, SKU, UPC, etc.)
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm">
                            <input
                                type="text"
                                name="search"
                                id="search"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="Enter identifier"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex items-end">
                        <button
                            type="submit"
                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            disabled={loading}
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Error Display */}
            {error && (
                <div className="mt-4 rounded-md bg-red-50 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                    </div>
                </div>
            )}

            {/* Inventory Table */}
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">ID</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ASIN</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">SKU</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Identifier</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Condition</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Sale Pending</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Notes</th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {inventoryItems.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan="9" className="py-4 pl-4 pr-3 text-sm text-gray-500 text-center">
                                            No inventory items found
                                        </td>
                                    </tr>
                                ) : (
                                    inventoryItems.map((item) => (
                                        <tr key={item.id}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">{item.id}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.asin}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.seller_sku}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.identifier}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {item.sku_condition === 1 ? (
                                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                                        Like New
                                                    </span>
                                                ) : item.sku_condition === 2 ? (
                                                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                                                        Very Good
                                                    </span>
                                                ) : item.sku_condition === 3 ? (
                                                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                                                        Good
                                                    </span>
                                                ) : item.sku_condition === 4 ? (
                                                    <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                                                        Acceptable
                                                    </span>
                                                ) : item.sku_condition === 11 ? (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        New
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                                        {item.sku_condition}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <div className="relative">
                                                    <select
                                                        value={item.location || ''}
                                                        onChange={(e) => handleLocationChange(item.id, e.target.value)}
                                                        className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                                                            updateStatus[item.id] === 'error' 
                                                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                                                : updateStatus[item.id] === 'success'
                                                                ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                                                                : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                                                        }`}
                                                        disabled={updateStatus[item.id] === 'loading'}
                                                    >
                                                        <option value="">Select Location</option>
                                                        {locations.map((loc) => (
                                                            <option key={loc.id} value={loc.location}>
                                                                {loc.location}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {updateStatus[item.id] === 'loading' && (
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {updateStatus[item.id] === 'success' && (
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {updateStatus[item.id] === 'error' && (
                                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {item.sale_pending ? (
                                                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                                                        Pending
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                        Available
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{item.notes}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LocalInventoryAdjustment;